import type { Recipe } from '../types';
import { EmojiBadge } from './EmojiBadge';
import { formatTimeLine } from '../lib/format';

const STATUS_LABEL: Record<NonNullable<Recipe['status']>, string> = {
  yes: 'Yes', maybe: 'Maybe', no: 'No',
};
const STATUS_BG: Record<NonNullable<Recipe['status']>, string> = {
  yes: 'var(--sage)', maybe: 'var(--amber-bg)', no: '#F5EDEF',
};
const STATUS_COLOR: Record<NonNullable<Recipe['status']>, string> = {
  yes: 'var(--sage-text)', maybe: 'var(--amber-text)', no: 'var(--plum-soft)',
};

export function RecipeCard(props: {
  recipe: Recipe;
  lastSeen?: { rating: 'up' | 'meh' | 'down' | null; daysAgo: number | null };
  onClick: () => void;
  onArchive?: () => void;
  onRestore?: () => void;
  onAddToPlan?: (recipeId: string) => void;
  onCurrentWeek?: boolean;
}) {
  const { recipe } = props;
  const archived = recipe.archived;
  const showSourcePill = !!recipe.source_name && !archived;
  const showHoverOverlay = !archived;
  const timeLine = showHoverOverlay
    ? formatTimeLine(recipe.prep_minutes, recipe.cook_minutes, null)
    : '';

  return (
    <div
      className={`recipe-card${props.onCurrentWeek ? ' recipe-card-on-plan' : ''}`}
      onClick={props.onClick}
      style={{
        background: 'white', overflow: 'hidden',
        cursor: 'pointer',
        position: 'relative',
        opacity: archived ? 0.55 : 1,
      }}
    >
      <div className="recipe-image-wrap" style={{ position: 'relative' }}>
        <EmojiBadge category={recipe.category} cuisine={recipe.cuisine} imageUrl={recipe.image_url} imageFocus={recipe.image_focus} height={240} />

        {/* Top-left: frosted name pill (always visible). Split parenthetical
            clarifier ("Bánh Canh (Thick Noodle Soup)") so the native name can
            stay bold while the English clarifier shrinks — keeps everything
            on one row. */}
        <div className="recipe-title-pill" aria-hidden="true">
          <div className="recipe-title-name">
            <span className="recipe-title-main">{splitName(recipe.name).main}</span>
            {splitName(recipe.name).sub && (
              <span className="recipe-title-sub"> · {splitName(recipe.name).sub}</span>
            )}
          </div>
          {archived && <div className="recipe-title-source">Archived</div>}
        </div>

        {/* Bottom-right: small source pill */}
        {recipe.source_name && !archived && (
          <span className="recipe-source-pill" aria-hidden="true">{recipe.source_name}</span>
        )}

        {/* Bottom-left: "On plan" indicator when this recipe is in the current week */}
        {props.onCurrentWeek && !archived && (
          <span className="recipe-on-plan-pill" aria-label="On this week's plan">✓ On plan</span>
        )}

        {/* Top-right cluster: status pill + last-rating + archive/restore.
            z-index lifts above the hover overlay so the archive × stays clickable on hover. */}
        <div className="recipe-top-cluster" style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 6, alignItems: 'center', zIndex: 3 }}>
          {recipe.status && !archived && (
            <span style={{
              background: STATUS_BG[recipe.status], color: STATUS_COLOR[recipe.status],
              fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 999,
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            }}>{STATUS_LABEL[recipe.status]}</span>
          )}
          {props.lastSeen?.rating && !archived && (
            <span style={{
              background: 'rgba(255,255,255,0.92)',
              color: 'var(--plum)',
              fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 999,
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            }}>
              {ratingEmoji(props.lastSeen.rating)}
              {props.lastSeen.daysAgo !== null && ` ${props.lastSeen.daysAgo}d`}
            </span>
          )}
          {archived && props.onRestore ? (
            <button
              className="recipe-card-action"
              aria-label={`Restore ${recipe.name}`}
              title={`Restore ${recipe.name}`}
              onClick={e => { e.stopPropagation(); props.onRestore!(); }}
              style={{
                padding: '5px 10px', borderRadius: 999,
                background: 'rgba(255,255,255,0.92)', color: 'var(--pink-deep)',
                border: 'none', fontSize: 11, fontWeight: 700,
                boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
              }}
            >Restore</button>
          ) : props.onArchive && (
            <button
              className="recipe-card-action"
              aria-label={`Archive ${recipe.name}`}
              title={`Archive ${recipe.name}`}
              onClick={e => { e.stopPropagation(); props.onArchive!(); }}
              style={{
                width: 28, height: 28, borderRadius: 999,
                background: 'rgba(255,255,255,0.92)',
                color: 'var(--pink-deep)', border: 'none', padding: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
              }}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                <path d="M2 2 L10 10 M10 2 L2 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
              </svg>
            </button>
          )}
        </div>

        {/* Desktop hover detail overlay (CSS-gated to (hover: hover)) */}
        {showHoverOverlay && (
          <div className="recipe-hover-overlay" aria-hidden="true">
            <div className="recipe-hover-name">
              <span className="recipe-hover-main">{splitName(recipe.name).main}</span>
              {splitName(recipe.name).sub && (
                <span className="recipe-hover-sub"> · {splitName(recipe.name).sub}</span>
              )}
            </div>
            {recipe.source_name && (
              <div className="recipe-hover-source">{recipe.source_name}</div>
            )}
            {timeLine && <div className="recipe-hover-time">{timeLine}</div>}
            {recipe.description && (
              <div className="recipe-hover-desc">{recipe.description}</div>
            )}
            {props.onAddToPlan && (
              <button
                type="button"
                className="recipe-hover-add"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  props.onAddToPlan!(recipe.id);
                }}
              >+ Add to plan</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ratingEmoji(r: 'up' | 'meh' | 'down'): string {
  return r === 'up' ? '👍' : r === 'meh' ? '😐' : '👎';
}

// "Bánh Canh (Thick Noodle Soup)" → { main: "Bánh Canh", sub: "Thick Noodle Soup" }
// "Mapo Tofu" → { main: "Mapo Tofu", sub: null }
function splitName(name: string): { main: string; sub: string | null } {
  const m = /^(.+?)\s*\(([^)]+)\)\s*$/.exec(name);
  if (m) return { main: m[1]!.trim(), sub: m[2]!.trim() };
  return { main: name, sub: null };
}
